/**
 * tale-builder.js: tiny entry (~1 KB) loaded on the product page.
 * It only wires the "Make your book" button. The builder itself (JS + CSS) is
 * fetched on first intent (hover / touch / focus), on idle (if the block allows
 * prefetch), or on click, so the PDP pays almost nothing for it.
 */
const STORAGE_PREFIX = 'tb:v1:'; // must match tb-store.js

function initRoot(root) {
  if (root.dataset.tbReady) return;
  root.dataset.tbReady = '1';
  const btn = root.querySelector('[data-tb-open]');
  const cfgEl = root.querySelector('script[data-tb-config]');
  if (!btn || !cfgEl) return;

  const base = new URL('.', import.meta.url);
  const moduleUrl = root.dataset.moduleUrl || new URL('tb-app.js', base).href;
  const cssUrl = root.dataset.cssUrl || new URL('tale-builder.css', base).href;
  // read at click time so the theme editor / dev panel can change config without a reload
  const readConfig = () => JSON.parse(cfgEl.textContent);

  let modPromise = null;
  let cssPromise = null;
  const loadCss = () =>
    (cssPromise ||= new Promise((resolve) => {
      if (document.querySelector('link[data-tb-css]')) return resolve();
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = cssUrl;
      link.dataset.tbCss = '';
      link.onload = link.onerror = () => resolve();
      document.head.append(link);
    }));
  const load = () => (modPromise ||= Promise.all([import(moduleUrl), loadCss()]).then(([m]) => m));

  // "Continue your book" if there is saved progress
  try {
    const cfg = readConfig();
    const raw = localStorage.getItem(`${STORAGE_PREFIX}${cfg.productHandle || 'book'}`);
    if (raw) {
      const s = JSON.parse(raw);
      const has = s && s.answers && Object.values(s.answers).some((v) => String(v).trim());
      const label = btn.querySelector('[data-tb-label]');
      if (has && label && root.dataset.resumeLabel) label.textContent = root.dataset.resumeLabel;
    }
  } catch {
    /* storage unavailable */
  }

  const warm = () => load().catch(() => {});
  ['pointerenter', 'touchstart', 'focus'].forEach((ev) => btn.addEventListener(ev, warm, { once: true, passive: true }));

  if (root.dataset.prefetch === 'true' && !navigator.connection?.saveData && !/2g/.test(navigator.connection?.effectiveType || '')) {
    const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 2500));
    idle(warm, { timeout: 6000 });
  }

  btn.addEventListener('click', async () => {
    if (btn.getAttribute('aria-busy') === 'true') return;
    btn.setAttribute('aria-busy', 'true');
    try {
      const mod = await load();
      mod.openBuilder({ config: readConfig(), trigger: btn });
    } catch (err) {
      console.error('[tale-builder] failed to load', err);
      modPromise = null;
      btn.dispatchEvent(new CustomEvent('tale-builder:error', { bubbles: true, detail: err }));
      // graceful fallback: let the shopper know rather than doing nothing
      // the note under the button is optional, so create one if the block has none
      let note = root.querySelector('[data-tb-note]');
      if (!note) {
        note = document.createElement('p');
        note.className = 'tb-trigger__note';
        note.dataset.tbNote = '';
        note.setAttribute('role', 'alert');
        root.insertBefore(note, cfgEl);
      }
      note.textContent = 'Sorry, the builder could not load. Please refresh and try again.';
    } finally {
      btn.removeAttribute('aria-busy');
    }
  });
}

const scan = () => document.querySelectorAll('[data-tb-root]').forEach(initRoot);
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scan, { once: true });
else scan();
// theme editor: sections re-render dynamically
document.addEventListener('shopify:section:load', scan);
document.addEventListener('shopify:block:select', scan);
